import { useQuery } from '@tanstack/react-query'
import { api } from './api'
import { User } from './users.service'

export type Document = {
  _id: string
  createdAt: string
  name: string
  isFolder: boolean
  owner: User | string
  isFavorite: boolean
  documentType: string | {
    _id: string
    name: string
    metadata?: Array<{
      name: string
      _id: string
    }>
    groups?: any[]
    deleted: boolean
    createdAt: string
    updatedAt: string
    __v: number
  }
  url?: string
  path: string
  mimetype: string
  size: string
  pages?: number
  ocrStatus?: 'pending' | 'processing' | 'completed' | 'failed'
  extractedText?: string
  ocrSnippet?: string
  documentTypeWithData?: {
    metadata?: Array<{
      name: string
      value?: string
    }>
    documentType?: {
      name: string
    }
  }
  simDataFound?: boolean
  simData?: any
}

type Documents = {
  data: Document[]
  page: number
  petPage: number
  totalPages: number
  total: number
}

async function fetchDocuments(
  page?: number,
  perPage?: number,
  search?: string,
  sort?: string,
  directory?: string[],
  query?: any,
): Promise<Documents> {
  let sortName: any = 'name'
  let order: string = 'asc'

  if (sort) {
    var keyNames = Object.keys(sort)
    sortName = keyNames[0]
    order = sort[sortName]
  }

  // Tratar pasta raiz de forma especial
  const directoryParam = directory && directory.length === 1 && directory[0] === '/' 
    ? [] 
    : directory

  const res = await api.get('/documents', {
    params: {
      page,
      sort: sortName,
      order: order === 'desc' ? -1 : 1,
      perPage,
      search,
      textSearch: search,
      directory: JSON.stringify(directoryParam),
      ...query,
    },
  })
  
  return res.data
}

function useDocuments(
  page: number | undefined,
  perPage: number | undefined,
  search: string | undefined,
  sort: string | undefined,
  directory: string[],
  query?: any,
) {
  return useQuery(
    ['documents', page, perPage, search, sort, directory, query],
    () => fetchDocuments(page, perPage, search, sort, directory, query),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutos
      cacheTime: 10 * 60 * 1000, // 10 minutos
    },
  )
}

// Função para busca global em todos os documentos acessíveis
async function fetchGlobalSearch(
  page?: number,
  perPage?: number,
  search?: string,
  sort?: string,
  query?: any,
): Promise<Documents> {
  let sortName: any = 'name'
  let order: string = 'asc'

  if (sort) {
    var keyNames = Object.keys(sort)
    sortName = keyNames[0]
    order = sort[sortName]
  }

  const res = await api.get('/documents/global-search', {
    params: {
      page,
      sort: sortName,
      order: order === 'desc' ? -1 : 1,
      perPage,
      search,
      textSearch: search,
      ...query,
    },
  })
  
  return res.data
}

function useGlobalSearch(
  page: number | undefined,
  perPage: number | undefined,
  search: string | undefined,
  sort: string | undefined,
  query?: any,
) {
  return useQuery(
    ['global-search', page, perPage, search, sort, query],
    () => fetchGlobalSearch(page, perPage, search, sort, query),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutos
      cacheTime: 10 * 60 * 1000, // 10 minutos
    },
  )
}

// Função para obter link de compartilhamento de um documento
async function fetchDocumentShareLink(documentId: string): Promise<{ success: boolean; shareLink: string; document: any }> {
  const res = await api.get(`/documents/${documentId}?generateLink=true`)
  return res.data
}

// Hook para obter link de compartilhamento
function useDocumentShareLink(documentId: string | null) {
  return useQuery(
    ['documentShareLink', documentId],
    () => fetchDocumentShareLink(documentId!),
    {
      enabled: !!documentId,
      staleTime: 5 * 60 * 1000, // 5 minutos
    },
  )
}

export { useDocumentShareLink, fetchDocumentShareLink, useGlobalSearch, fetchGlobalSearch }
export default useDocuments
